import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSSEWithFallback } from "@/hooks/useSSEWithFallback";
import { toast } from "sonner";
import { Radio, Power, MapPin, Users, Clock, ChevronDown, AlertTriangle, Zap, X, Check, Coffee, Gift, Loader2 } from "lucide-react";
import TipOfTheDay from "@/components/shared/TipOfTheDay";

const TIME_PILLS = [5, 10, 15, 20, 30];
const EXTEND_OPTIONS = [
  { mins: 15, label: "+15 min" },
  { mins: 30, label: "+30 min" },
  { mins: 60, label: "+1 hr" },
];

export default function VendorGoLive() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [selectedMinutes, setSelectedMinutes] = useState(15);
  const [customMinutes, setCustomMinutes] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [timeActive, setTimeActive] = useState(0);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({ notice: 15, punchMode: "trust_based", compensation: false });
  const [showBatteryWarn, setShowBatteryWarn] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [quickAction, setQuickAction] = useState(null);
  const [quickInput, setQuickInput] = useState("");
  const [closingMinutes, setClosingMinutes] = useState(15);
  const [locationInput, setLocationInput] = useState({ lat: "", lng: "", address: "" });
  const [pendingPunches, setPendingPunches] = useState([]);
  const [pendingRedemptions, setPendingRedemptions] = useState([]);
  const [confirmingPunch, setConfirmingPunch] = useState(null);
  const [weather, setWeather] = useState(null);
  const [nearbyTruckCount, setNearbyTruckCount] = useState(null);
  const wakeLockRef = useRef(null);
  const timerRef = useRef(null);

  // Poll live status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/live-status`);
      setStatus(res.data);
      if (res.data.active && res.data.time_active_seconds) {
        setTimeActive(res.data.time_active_seconds);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  // Fetch pending punches (for vendor confirmation tray)
  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/punchcard/vendor-pending`);
      setPendingPunches(res.data.pending_punches || []);
      setPendingRedemptions(res.data.pending_redemptions || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchStatus(); fetchPending(); }, [fetchStatus, fetchPending]);

  // Fetch weather + competitor count
  useEffect(() => {
    const fetchWeatherAndCompetitors = async () => {
      try {
        // Get vendor's business location
        const dashRes = await axios.get(`${API}/vendor/dashboard`);
        const bid = dashRes.data?.business_id;
        if (bid) {
          const wRes = await axios.get(`${API}/weather/business/${bid}`);
          if (wRes.data?.weather) setWeather(wRes.data.weather);
        }
        const lat = dashRes.data?.home_base_lat;
        const lng = dashRes.data?.home_base_lng;
        if (lat && lng) {
          const cRes = await axios.get(`${API}/vendor/nearby-trucks-count?lat=${lat}&lng=${lng}`);
          setNearbyTruckCount(cRes.data?.count ?? null);
        }
      } catch {}
    };
    fetchWeatherAndCompetitors();
  }, []);

  // SSE-powered live status (replaces 60s polling)
  const fetchAll = useCallback(async () => {
    await fetchStatus();
    await fetchPending();
    try {
      const dashRes = await axios.get(`${API}/vendor/dashboard`);
      const lat = dashRes.data?.home_base_lat;
      const lng = dashRes.data?.home_base_lng;
      if (lat && lng) {
        const cRes = await axios.get(`${API}/vendor/nearby-trucks-count?lat=${lat}&lng=${lng}`);
        setNearbyTruckCount(cRes.data?.count ?? null);
      }
    } catch {}
  }, [fetchStatus, fetchPending]);

  useSSEWithFallback(
    "vendor_session_changed",
    (data) => { fetchStatus(); },
    fetchAll,
  );

  useSSEWithFallback(
    "nearby_trucks_count_changed",
    (data) => { setNearbyTruckCount(data.new_count ?? null); },
    null, // initial fetch already handled by fetchAll
  );

  useSSEWithFallback(
    "punch_pending_changed",
    (data) => { fetchPending(); },
    null, // initial fetch already handled by fetchAll
  );

  // Time active counter
  useEffect(() => {
    if (status?.active && status?.status === "live") {
      timerRef.current = setInterval(() => setTimeActive(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [status?.active, status?.status]);

  // Wake lock management (Task 5)
  useEffect(() => {
    if (status?.active && status?.status === "live") {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
    return () => releaseWakeLock();
  }, [status?.active, status?.status]);

  const requestWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch { /* fail silently */ }
  };

  const releaseWakeLock = () => {
    try { wakeLockRef.current?.release(); wakeLockRef.current = null; } catch { /* ignore */ }
  };

  // Countdown timer for "Going Live In" state
  useEffect(() => {
    if (!countdown || countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // Check battery (Task 8)
  const checkBattery = async () => {
    try {
      if (navigator.getBattery) {
        const bat = await navigator.getBattery();
        const level = Math.round(bat.level * 100);
        setBatteryLevel(level);
        if (level < 30) {
          setShowBatteryWarn(true);
          return true; // Shows warning
        }
      }
    } catch { /* ignore */ }
    return false;
  };

  const handleGoLive = async () => {
    // First-time wizard check (Task 7)
    if (!user?.vendor_wizard_completed) {
      setShowWizard(true);
      return;
    }

    // Battery check (Task 8)
    const needsWarning = await checkBattery();
    if (needsWarning) return;

    await executeGoLive();
  };

  const executeGoLive = async () => {
    setActing(true);
    try {
      let lat = null, lng = null, addr = null;
      // Try to get current location
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* GPS not available */ }

      const mins = showCustom ? parseInt(customMinutes) || 15 : selectedMinutes;
      const res = await axios.post(`${API}/vendor/go-live`, {
        location_lat: lat, location_lng: lng, location_address: addr,
        goes_live_in_minutes: mins,
      });
      toast.success(`Going live in ${mins} minutes!`);
      setCountdown(mins * 60);
      fetchStatus();

      // Persistent notification (Task 6) — Android only Phase 1
      // iOS persistent notifications require different handling — Phase 2
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("BirthdayDeaLight — You are LIVE", {
            body: "Tap to return to vendor controls.",
            icon: "/logo192.png",
            tag: "vendor-live",
            requireInteraction: true,
          });
        }
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to go live");
    } finally { setActing(false); }
  };

  const handleEndService = async () => {
    if (!status?.session_id) return;
    setActing(true);
    try {
      await axios.post(`${API}/vendor/go-offline`, { session_id: status.session_id });
      toast.success("Service ended. See you next time!");
      releaseWakeLock();
      // Dismiss persistent notification
      try { if ("Notification" in window) { /* Close by tag not supported, but new notification replaces */ } } catch {}
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to go offline");
    } finally { setActing(false); }
  };

  const handleExtend = async (mins) => {
    if (!status?.session_id) return;
    try {
      const res = await axios.post(`${API}/vendor/go-live/extend`, { session_id: status.session_id, additional_minutes: mins });
      const endTime = res.data.scheduled_end_time ? new Date(res.data.scheduled_end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
      toast.success(`Extended — wrapping up at ${endTime}`);
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to extend");
    }
  };

  // Confirm a pending punch (tap_to_confirm mode)
  const handleConfirmPunch = async (punchId) => {
    setConfirmingPunch(punchId);
    try {
      await axios.post(`${API}/punchcard/vendor-confirm`, { punch_id: punchId });
      toast.success("Punch confirmed!");
      fetchPending();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to confirm");
    } finally { setConfirmingPunch(null); }
  };

  // Confirm a pending redemption
  const handleConfirmRedeem = async (redemptionId) => {
    setConfirmingPunch(redemptionId);
    try {
      await axios.post(`${API}/punchcard/vendor-confirm-redeem`, { redemption_id: redemptionId });
      toast.success("Reward confirmed!");
      fetchPending();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to confirm");
    } finally { setConfirmingPunch(null); }
  };

  const handleQuickAction = async (action) => {
    if (!status?.session_id) return;
    try {
      if (action === "running-low") {
        await axios.post(`${API}/vendor/quick-action/running-low`, { item_name: quickInput });
        toast.success(`Flagged "${quickInput}" as running low`);
      } else if (action === "sold-out") {
        await axios.post(`${API}/vendor/quick-action/sold-out`, { item_name: quickInput });
        toast.success(`Marked "${quickInput}" as sold out`);
      } else if (action === "closing-early") {
        await axios.post(`${API}/vendor/go-offline`, { session_id: status.session_id, closing_in_minutes: closingMinutes });
        toast.success(`Closing in ${closingMinutes} minutes`);
        fetchStatus();
      } else if (action === "update-location") {
        let lat = parseFloat(locationInput.lat), lng = parseFloat(locationInput.lng);
        if (!lat || !lng) {
          try {
            const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
            lat = pos.coords.latitude;
            lng = pos.coords.longitude;
          } catch { toast.error("Could not get GPS location"); return; }
        }
        await axios.post(`${API}/vendor/quick-action/update-location`, { session_id: status.session_id, lat, lng, address: locationInput.address });
        toast.success("Location updated — followers notified");
        fetchStatus();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
    setQuickAction(null);
    setQuickInput("");
  };

  const handleWizardComplete = async () => {
    try {
      await axios.post(`${API}/vendor/setup-wizard`, {
        default_notice_minutes: wizardData.notice,
        punch_confirmation_mode: wizardData.punchMode,
        sold_out_compensation_enabled: wizardData.compensation,
      });
      setShowWizard(false);
      setSelectedMinutes(wizardData.notice);
      // Now proceed with battery check and go live
      const needsWarning = await checkBattery();
      if (!needsWarning) await executeGoLive();
    } catch (err) {
      toast.error("Failed to save settings");
    }
  };

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Radio className="w-8 h-8 text-[#F59E0B] animate-pulse" /></div>;

  const isLive = status?.active && (status?.status === "live" || status?.status === "closing");
  const isScheduled = status?.active && status?.status === "scheduled";

  // ── STATE B: LIVE ──
  if (isLive || isScheduled) {
    return (
      <div className="min-h-screen px-4 pt-14 pb-24 flex flex-col" data-testid="vendor-go-live-state-b">
        {/* Live indicator */}
        <div className="text-center mb-4">
          {status.status === "closing" ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/40">
              <div className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-amber-400 font-black text-sm tracking-wider">CLOSING SOON</span>
            </div>
          ) : status.status === "scheduled" ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/40">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 font-black text-sm">GOING LIVE IN {countdown ? formatTime(countdown) : "..."}</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#22C55E]/20 border border-[#22C55E]/40">
              <div className="w-3 h-3 rounded-full bg-[#22C55E] animate-pulse" />
              <span className="text-[#22C55E] font-black text-sm tracking-wider">LIVE NOW</span>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center gap-2 justify-center mb-2 text-white/60">
          <MapPin className="w-3.5 h-3.5" />
          <span className="text-xs" data-testid="live-location">{status.location_address || "No location set"}</span>
        </div>

        {/* Time active */}
        <div className="text-center mb-2">
          <span className="text-3xl font-mono font-bold text-white" data-testid="time-active">{formatTime(timeActive)}</span>
          <p className="text-[10px] text-white/40 mt-0.5">Time active</p>
        </div>

        {/* END SERVICE button */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <button
            onClick={handleEndService}
            disabled={acting}
            className="w-80 h-80 rounded-full bg-[#EF4444] flex items-center justify-center shadow-[0_0_60px_rgba(239,68,68,0.5),0_0_120px_rgba(239,68,68,0.2)] active:scale-90 transition-all"
            data-testid="end-service-button"
          >
            <Power className="w-20 h-20 text-white" />
          </button>
          <span className="text-3xl font-black text-[#EF4444] mt-4 tracking-[0.2em]">END SERVICE</span>
        </div>

        {/* Extend time pills */}
        <div className="flex gap-2 justify-center mb-4">
          {EXTEND_OPTIONS.map(opt => (
            <button key={opt.mins} onClick={() => handleExtend(opt.mins)}
              className="px-4 py-2 rounded-full bg-[#2C2C2E] text-white/70 text-xs font-semibold border border-white/10 hover:border-[#F59E0B]/50 hover:text-[#F59E0B] transition"
              data-testid={`extend-${opt.mins}`}>
              {opt.label}
            </button>
          ))}
        </div>

        {/* Quick Action Tray */}
        <div className="grid grid-cols-2 gap-2" data-testid="quick-action-tray">
          {[
            { id: "running-low", label: "Running Low", icon: AlertTriangle, color: "text-amber-400" },
            { id: "sold-out", label: "Sold Out", icon: X, color: "text-red-400" },
            { id: "closing-early", label: "Closing Early", icon: Clock, color: "text-orange-400" },
            { id: "update-location", label: "New Location", icon: MapPin, color: "text-blue-400" },
          ].map(qa => (
            <button key={qa.id} onClick={() => setQuickAction(qa.id)}
              className="flex items-center gap-2 p-3 rounded-xl bg-[#2C2C2E] border border-white/5 hover:border-white/15 transition"
              data-testid={`quick-action-${qa.id}`}>
              <qa.icon className={`w-4 h-4 ${qa.color}`} />
              <span className="text-xs text-white/70 font-medium">{qa.label}</span>
            </button>
          ))}
        </div>

        {/* ── Pending Punch Confirmations (Part 22J Task 3) ── */}
        {(pendingPunches.length > 0 || pendingRedemptions.length > 0) && (
          <div className="mt-4 space-y-2" data-testid="pending-punches-tray">
            <p className="text-xs font-bold text-white/50 uppercase tracking-wider flex items-center gap-1.5">
              <Coffee className="w-3.5 h-3.5 text-[#F59E0B]" />
              Pending Confirmations ({pendingPunches.length + pendingRedemptions.length})
            </p>

            {/* Pending redemptions first (higher priority) */}
            {pendingRedemptions.map(r => (
              <div key={r.punch_id} className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20" data-testid={`pending-redeem-${r.punch_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Gift className="w-4 h-4 text-amber-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-amber-300 truncate">{r.customer_name || "Customer"}</p>
                    <p className="text-[10px] text-white/40">Redeeming reward</p>
                  </div>
                </div>
                <button
                  onClick={() => handleConfirmRedeem(r.punch_id)}
                  disabled={confirmingPunch === r.punch_id}
                  className="px-4 py-2.5 rounded-lg bg-amber-500 text-[#1C1C1E] text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                  data-testid={`confirm-redeem-${r.punch_id}`}
                >
                  {confirmingPunch === r.punch_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Give Reward
                </button>
              </div>
            ))}

            {/* Pending punches */}
            {pendingPunches.map(p => (
              <div key={p.punch_id} className="flex items-center justify-between p-3 rounded-xl bg-[#2C2C2E] border border-white/5" data-testid={`pending-punch-${p.punch_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Coffee className="w-4 h-4 text-[#F59E0B] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-white/70 truncate">{p.customer_name || "Customer"}</p>
                    <p className="text-[10px] text-white/30">{new Date(p.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleConfirmPunch(p.punch_id)}
                  disabled={confirmingPunch === p.punch_id}
                  className="px-4 py-2.5 rounded-lg bg-[#22C55E] text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                  data-testid={`confirm-punch-${p.punch_id}`}
                >
                  {confirmingPunch === p.punch_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Confirm
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Quick Action Modal */}
        {quickAction && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60" onClick={() => setQuickAction(null)} data-testid="quick-action-modal">
            <div className="bg-[#2C2C2E] rounded-t-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
              <div className="w-8 h-1 rounded-full bg-white/20 mx-auto mb-2" />
              {(quickAction === "running-low" || quickAction === "sold-out") && (
                <>
                  <p className="text-sm font-bold text-white">{quickAction === "running-low" ? "Flag Item Running Low" : "Mark Item Sold Out"}</p>
                  <p className="text-[11px] text-white/40">Add your menu items in Settings to use this feature, or type item name below.</p>
                  <input value={quickInput} onChange={e => setQuickInput(e.target.value)} placeholder="Item name..."
                    className="w-full bg-[#1C1C1E] text-white text-sm rounded-lg px-3 py-2 border border-white/10 focus:border-[#F59E0B]/50 outline-none" data-testid="quick-action-input" />
                  <button onClick={() => handleQuickAction(quickAction)} disabled={!quickInput.trim()}
                    className="w-full py-2.5 rounded-lg bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm disabled:opacity-40">Confirm</button>
                </>
              )}
              {quickAction === "closing-early" && (
                <>
                  <p className="text-sm font-bold text-white">Closing Early</p>
                  <div className="flex gap-2">
                    {[10, 15, 20, 30].map(m => (
                      <button key={m} onClick={() => setClosingMinutes(m)}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${closingMinutes === m ? "bg-[#F59E0B] text-[#1C1C1E]" : "bg-[#1C1C1E] text-white/60 border border-white/10"}`}>
                        {m}m
                      </button>
                    ))}
                  </div>
                  <button onClick={() => handleQuickAction("closing-early")}
                    className="w-full py-2.5 rounded-lg bg-orange-500 text-white font-bold text-sm">Close in {closingMinutes} min</button>
                </>
              )}
              {quickAction === "update-location" && (
                <>
                  <p className="text-sm font-bold text-white">Update Location</p>
                  <button onClick={() => handleQuickAction("update-location")}
                    className="w-full py-2.5 rounded-lg bg-blue-500 text-white font-bold text-sm flex items-center justify-center gap-2">
                    <MapPin className="w-4 h-4" /> Use Current GPS
                  </button>
                  <input value={locationInput.address} onChange={e => setLocationInput(p => ({...p, address: e.target.value}))}
                    placeholder="Or enter address..."
                    className="w-full bg-[#1C1C1E] text-white text-sm rounded-lg px-3 py-2 border border-white/10 outline-none" />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── STATE A: OFFLINE ──
  return (
    <div className="min-h-screen px-4 pt-14 pb-24 flex flex-col" data-testid="vendor-go-live-state-a">
      <TipOfTheDay pageKey="vendor_go_live" />
      {/* Status */}
      <div className="text-center mb-6">
        <p className="text-2xl font-black text-white/20 tracking-widest" data-testid="offline-label">OFFLINE</p>
        {status?.last_session?.went_offline_at && (
          <p className="text-[10px] text-white/30 mt-1">
            Last active: {new Date(status.last_session.went_offline_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* GO LIVE button */}
      <div className="flex-1 flex flex-col items-center justify-center">
        {countdown && countdown > 0 ? (
          <>
            <div className="w-40 h-40 rounded-full bg-[#22C55E]/20 border-4 border-[#22C55E] flex items-center justify-center animate-pulse">
              <span className="text-3xl font-mono font-bold text-[#22C55E]">{formatTime(countdown)}</span>
            </div>
            <span className="text-base text-[#22C55E] mt-3 font-black tracking-wider">GOING LIVE IN</span>
          </>
        ) : (
          <>
            <button
              onClick={handleGoLive}
              disabled={acting}
              className="w-80 h-80 rounded-full bg-[#22C55E] flex items-center justify-center shadow-[0_0_60px_rgba(34,197,94,0.5),0_0_120px_rgba(34,197,94,0.2)] active:scale-90 transition-all hover:shadow-[0_0_80px_rgba(34,197,94,0.6),0_0_160px_rgba(34,197,94,0.3)]"
              data-testid="go-live-button"
            >
              {acting ? <Radio className="w-20 h-20 text-white animate-spin" /> : <Radio className="w-20 h-20 text-white" />}
            </button>
            <span className="text-3xl font-black text-[#22C55E] mt-4 tracking-[0.2em]">GO LIVE</span>
          </>
        )}
      </div>

      {/* Time pills */}
      <div className="mb-4">
        <p className="text-[11px] text-white/40 text-center mb-2">Going live in</p>
        <div className="flex gap-2 justify-center flex-wrap">
          {TIME_PILLS.map(m => (
            <button key={m} onClick={() => { setSelectedMinutes(m); setShowCustom(false); }}
              className={`px-4 py-2 rounded-full text-xs font-bold transition ${
                !showCustom && selectedMinutes === m
                  ? "bg-[#F59E0B] text-[#1C1C1E]"
                  : "bg-[#2C2C2E] text-white/50 border border-white/10 hover:border-[#F59E0B]/40"
              }`}
              data-testid={`time-pill-${m}`}>
              {m} min
            </button>
          ))}
          <button onClick={() => setShowCustom(true)}
            className={`px-4 py-2 rounded-full text-xs font-bold transition ${
              showCustom ? "bg-[#F59E0B] text-[#1C1C1E]" : "bg-[#2C2C2E] text-white/50 border border-white/10"
            }`} data-testid="time-pill-custom">
            Custom
          </button>
        </div>
        {showCustom && (
          <div className="mt-2 flex justify-center">
            <input type="number" value={customMinutes} onChange={e => setCustomMinutes(e.target.value)}
              placeholder="Minutes" min="1" max="120"
              className="w-24 bg-[#2C2C2E] text-white text-center text-sm rounded-lg px-3 py-2 border border-[#F59E0B]/40 outline-none"
              data-testid="custom-minutes-input" />
          </div>
        )}
      </div>

      {/* Weather Awareness Strip (Task 4) */}
      {weather && (() => {
        const rain = weather.rain_probability_percent;
        const wind = weather.wind_mph;
        let stripBg, stripBorder, advisory;
        if (rain >= 70 || wind >= 30) {
          stripBg = "bg-[#DC2626]/15"; stripBorder = "border-[#DC2626]/30";
          advisory = { text: "Tough conditions today — consider going live tomorrow instead", color: "text-red-400" };
        } else if (rain >= 30 || wind >= 15) {
          stripBg = "bg-[#D97706]/15"; stripBorder = "border-[#D97706]/30";
          advisory = { text: "Weather may affect service — plan accordingly", color: "text-amber-400" };
        } else {
          stripBg = "bg-[#16A34A]/15"; stripBorder = "border-[#16A34A]/30";
          advisory = { text: "Great day to go live", color: "text-green-400" };
        }
        return (
          <div className="mb-4" data-testid="weather-strip">
            <div className={`${stripBg} rounded-xl p-3 border ${stripBorder}`}>
              <div className="flex items-center gap-3">
                <span className="text-2xl">{weather.condition_emoji}</span>
                <div className="flex-1">
                  <p className="text-white font-bold text-sm">{weather.temperature_f}°F</p>
                  <p className="text-white/40 text-[10px]">Feels like {weather.feels_like_f}°F</p>
                </div>
                <div className="text-right">
                  <p className="text-white/60 text-[10px]">Rain: {rain}%</p>
                  {weather.tomorrow_summary && (
                    <p className="text-white/40 text-[9px] mt-0.5">
                      Tomorrow: {weather.tomorrow_summary.condition_emoji} {weather.tomorrow_summary.temperature_high_f}°F — {weather.tomorrow_summary.condition.replace("_", " ")}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <p className={`text-[10px] ${advisory.color} text-center mt-1.5 font-medium`} data-testid="weather-advisory">{advisory.text}</p>
          </div>
        );
      })()}

      {/* Nearby Activity (Task 6) */}
      {nearbyTruckCount !== null && (
        <div className="mb-4" data-testid="nearby-activity">
          {nearbyTruckCount === 0 ? (
            <div className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#16A34A]/10">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span className="text-[11px] text-green-400 font-medium">You are the only truck live in your area</span>
            </div>
          ) : nearbyTruckCount <= 2 ? (
            <div className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#D97706]/10">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-[11px] text-amber-400 font-medium">{nearbyTruckCount} other truck{nearbyTruckCount > 1 ? "s" : ""} live nearby</span>
            </div>
          ) : (
            <button
              onClick={() => navigate("/live-trucks")}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#DC2626]/10"
              data-testid="nearby-trucks-map-btn"
            >
              <div className="w-2 h-2 rounded-full bg-red-400" />
              <span className="text-[11px] text-red-400 font-medium">Busy area — {nearbyTruckCount} trucks live nearby. Tap to see map</span>
            </button>
          )}
        </div>
      )}

      {/* First-time Wizard (Task 7) */}
      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="setup-wizard">
          <div className="bg-[#2C2C2E] rounded-2xl w-full max-w-sm mx-4 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-white">Quick Setup ({wizardStep}/3)</p>
              <div className="flex gap-1">
                {[1,2,3].map(s => <div key={s} className={`w-6 h-1 rounded-full ${s <= wizardStep ? "bg-[#F59E0B]" : "bg-white/10"}`} />)}
              </div>
            </div>

            {wizardStep === 1 && (
              <div className="space-y-3">
                <p className="text-xs text-white/60">How many minutes before opening should your followers be notified?</p>
                <div className="flex gap-2 flex-wrap">
                  {TIME_PILLS.map(m => (
                    <button key={m} onClick={() => setWizardData(d => ({...d, notice: m}))}
                      className={`px-4 py-2 rounded-full text-xs font-bold transition ${wizardData.notice === m ? "bg-[#F59E0B] text-[#1C1C1E]" : "bg-[#1C1C1E] text-white/50 border border-white/10"}`}>
                      {m} min
                    </button>
                  ))}
                </div>
              </div>
            )}

            {wizardStep === 2 && (
              <div className="space-y-2">
                <p className="text-xs text-white/60">How do you want to confirm customer punch cards?</p>
                {[
                  { val: "trust_based", label: "Trust-based", desc: "Customer scans your QR — punch awarded automatically after dwell time. No action needed from you." },
                  { val: "return_to_zone", label: "Return to zone", desc: "Customer scans your QR — punch awarded automatically when they come back to pick up their order." },
                  { val: "tap_to_confirm", label: "Tap to confirm", desc: "You tap approve when you hand over the food. Most accurate." },
                ].map(opt => (
                  <button key={opt.val} onClick={() => setWizardData(d => ({...d, punchMode: opt.val}))}
                    className={`w-full text-left p-3 rounded-lg transition ${wizardData.punchMode === opt.val ? "bg-[#F59E0B]/10 border border-[#F59E0B]/40" : "bg-[#1C1C1E] border border-white/5"}`}>
                    <p className={`text-xs font-bold ${wizardData.punchMode === opt.val ? "text-[#F59E0B]" : "text-white/70"}`}>{opt.label}</p>
                    <p className="text-[10px] text-white/40 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            )}

            {wizardStep === 3 && (
              <div className="space-y-3">
                <p className="text-xs text-white/60">Want to offer a free punch when you sell out of an item?</p>
                <p className="text-[10px] text-white/30">When you mark an item as sold out, nearby customers automatically receive one free punch as an apology.</p>
                <div className="flex gap-3">
                  <button onClick={() => setWizardData(d => ({...d, compensation: true}))}
                    className={`flex-1 py-3 rounded-lg font-bold text-sm transition ${wizardData.compensation ? "bg-[#22C55E] text-white" : "bg-[#1C1C1E] text-white/50 border border-white/10"}`}>
                    Yes
                  </button>
                  <button onClick={() => setWizardData(d => ({...d, compensation: false}))}
                    className={`flex-1 py-3 rounded-lg font-bold text-sm transition ${!wizardData.compensation ? "bg-[#EF4444] text-white" : "bg-[#1C1C1E] text-white/50 border border-white/10"}`}>
                    No
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              {wizardStep > 1 && (
                <button onClick={() => setWizardStep(s => s - 1)} className="flex-1 py-2.5 rounded-lg bg-[#1C1C1E] text-white/60 font-semibold text-sm">Back</button>
              )}
              {wizardStep < 3 ? (
                <button onClick={() => setWizardStep(s => s + 1)} className="flex-1 py-2.5 rounded-lg bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm">Next</button>
              ) : (
                <button onClick={handleWizardComplete} className="flex-1 py-2.5 rounded-lg bg-[#22C55E] text-white font-bold text-sm flex items-center justify-center gap-1">
                  <Check className="w-4 h-4" /> Done — Go Live
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Battery Warning (Task 8) */}
      {showBatteryWarn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="battery-warning">
          <div className="bg-[#2C2C2E] rounded-2xl w-full max-w-sm mx-4 p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              <p className="text-sm font-bold text-white">Low Battery</p>
            </div>
            <p className="text-xs text-white/60">
              Your battery is at {batteryLevel}%. Consider plugging in before going live so your customers can always find you.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowBatteryWarn(false)} className="flex-1 py-2.5 rounded-lg bg-[#1C1C1E] text-white/60 font-semibold text-sm">Cancel</button>
              <button onClick={() => { setShowBatteryWarn(false); executeGoLive(); }} className="flex-1 py-2.5 rounded-lg bg-[#22C55E] text-white font-bold text-sm" data-testid="go-live-anyway">
                Go live anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
